import { useCallback, useEffect, useState } from 'react';
import {
  deleteConnection,
  getConnections,
  IntegrationConnectionStatus,
  IntegrationProvider,
  startConnection,
} from '@core/api/integrations/integrationsApi';

const PROVIDER: IntegrationProvider = 'microsoft';

export type OutlookConnectionStatus = IntegrationConnectionStatus | 'none';

const getErrorCode = (err: unknown): string | undefined => {
  const axiosErr = err as {
    response?: { data?: { errors?: Array<{ code?: string }> } };
  };
  return axiosErr?.response?.data?.errors?.[0]?.code;
};

const useOutlookConnect = () => {
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [status, setStatus] = useState<OutlookConnectionStatus>('none');
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  const refreshConnectionStatus = useCallback(async () => {
    setIsLoadingStatus(true);
    try {
      const { connections } = await getConnections();
      const microsoft = connections.find(
        (connection) => connection.provider === PROVIDER
      );
      if (!microsoft || microsoft.status === 'revoked') {
        setStatus('none');
        setAccountEmail(null);
        return;
      }
      setStatus(microsoft.status);
      setAccountEmail(microsoft.accountEmail);
    } catch {
      setStatus('none');
      setAccountEmail(null);
    } finally {
      setIsLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    refreshConnectionStatus();
  }, [refreshConnectionStatus]);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setErrorCode(null);
    try {
      const { authorizeUrl } = await startConnection(PROVIDER);
      window.location.assign(authorizeUrl);
    } catch (err) {
      setErrorCode(getErrorCode(err) || 'connect_failed');
      setIsConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setIsDisconnecting(true);
    setErrorCode(null);
    try {
      await deleteConnection(PROVIDER);
      setStatus('none');
      setAccountEmail(null);
    } catch (err) {
      setErrorCode(getErrorCode(err) || 'disconnect_failed');
    } finally {
      setIsDisconnecting(false);
    }
  }, []);

  const markConnected = useCallback(() => {
    setStatus('active');
  }, []);

  return {
    errorCode,
    isConnecting,
    isDisconnecting,
    status,
    accountEmail,
    isConnected: status === 'active',
    needsReauth: status === 'needs_reauth',
    isLoadingStatus,
    connect,
    disconnect,
    markConnected,
    refreshConnectionStatus,
    provider: PROVIDER,
  };
};

export default useOutlookConnect;
